import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Info, ArrowRight } from 'lucide-react';
import { adminService } from '../api/adminService';
import { useNotification } from '../contexts/NotificationContext';

export default function AdminSettings() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminService.configs.list();
      
      // Try different response structures due to custom fetch client return values
      let configsData = [];
      if (Array.isArray(response?.data)) {
        configsData = response.data;
      } else if (response?.data?.data) {
        configsData = response.data.data;
      } else if (Array.isArray(response)) {
        configsData = response;
      } else if (response?.data) {
        configsData = response.data;
      }

      if (configsData && configsData.length > 0) {
        setConfigs(configsData);
        
        // Convert configs to form data
        const formValues = {};
        configsData.forEach(config => {
          formValues[config.key] = config.value;
        });
        setFormData(prev => ({
          ...prev,
          ...formValues
        }));
      }
    } catch (error) {
      console.error('Error loading configs:', error);
      showError('Gagal memuat konfigurasi sistem');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async (configKey) => {
    try {
      setSaving(true);
      
      const config = configs.find(c => c.key === configKey);
      const newValue = formData[configKey];
      
      await adminService.configs.update({
        key: configKey,
        value: newValue,
        description: config?.description
      });
      
      if (configKey.startsWith("cta_")) {
        try {
          const cached = JSON.parse(localStorage.getItem("cta_configs_cache") || "{}");
          cached[configKey] = newValue;
          localStorage.setItem("cta_configs_cache", JSON.stringify(cached));
        } catch (err) {}
      }

      showSuccess('Konfigurasi berhasil disimpan');
      await loadConfigs();
    } catch (error) {
      console.error('Error saving config:', error);
      showError('Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  // Default configurations if backend doesn't provide them
  const defaultConfigs = [
    {
      key: 'site_name',
      value: formData['site_name'] || 'POS Asalink',
      description: 'Application name displayed to users'
    },
    {
      key: 'cta_banner_title',
      value: formData['cta_banner_title'] || 'Selamat Datang di ASA Link',
      description: 'Judul utama pada banner sambutan beranda utama'
    },
    {
      key: 'cta_banner_description',
      value: formData['cta_banner_description'] || 'Solusi digital untuk mendukung manajemen usaha Anda, dirancang untuk menyederhanakan operasional dan meningkatkan kontrol bisnis.',
      description: 'Deskripsi pada banner sambutan beranda utama'
    },
    {
      key: 'cta_button_text',
      value: formData['cta_button_text'] || 'Lihat Panduan',
      description: 'Teks tombol pada banner sambutan beranda utama'
    },
    {
      key: 'cta_button_link',
      value: formData['cta_button_link'] || 'https://asalink.id',
      description: 'Alamat tautan (URL) saat tombol Lihat Panduan diklik'
    },
    {
      key: 'max_users_per_tenant',
      value: formData['max_users_per_tenant'] || '50',
      description: 'Maximum number of users allowed per tenant'
    },
    {
      key: 'max_tenants_per_admin',
      value: formData['max_tenants_per_admin'] || '100',
      description: 'Maximum number of tenants a system admin can manage'
    },
    {
      key: 'app_version',
      value: formData['app_version'] || '1.0.0',
      description: 'Current application version'
    },
    {
      key: 'maintenance_mode',
      value: formData['maintenance_mode'] || 'false',
      description: 'Enable maintenance mode to block user access'
    }
  ];

  const allConfigs = React.useMemo(() => {
    const list = configs.length > 0 ? [...configs] : [...defaultConfigs];
    const existingKeys = new Set(list.map((c) => c.key));
    defaultConfigs.forEach((d) => {
      if (!existingKeys.has(d.key)) {
        list.push(d);
      }
    });
    return list;
  }, [configs, formData]);

  const renderConfigInput = (config) => {
    const value = formData[config.key] || config.value || '';
    
    if (config.key === 'maintenance_mode') {
      return (
        <select
          value={value}
          onChange={(e) => handleConfigChange(config.key, e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="false">Disabled</option>
          <option value="true">Enabled</option>
        </select>
      );
    }
    
    if (config.key === 'cta_banner_description') {
      return (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => handleConfigChange(config.key, e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      );
    }

    if (config.key.includes('max_') || config.key.includes('limit_')) {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => handleConfigChange(config.key, e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min="1"
        />
      );
    }
    
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleConfigChange(config.key, e.target.value)}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">System Settings</h1>
        <p className="text-gray-600">Configure system-wide settings and parameters</p>
      </div>

      <div className="max-w-4xl space-y-8">
        {/* Application Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <SettingsIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Application Settings</h2>
            </div>
            <p className="text-gray-600">General application configuration</p>
          </div>
          
          <div className="p-6 space-y-6">
            {allConfigs
              .filter(c => c.key === 'site_name' || c.key === 'app_version' || c.key === 'maintenance_mode')
              .map((config) => (
              <div key={config.key}>
                <div className="flex items-center gap-4 mb-2">
                  {renderConfigInput(config)}
                  <button
                    onClick={() => handleSave(config.key)}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-500">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-700">{config.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p>{config.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Banner & Panduan Pengguna */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <SettingsIcon className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-gray-900">Konfigurasi CTA & Panduan Pengguna (Beranda)</h2>
            </div>
            <p className="text-gray-600">Atur teks banner sambutan dan link tautan tombol Lihat Panduan di beranda utama</p>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Live Preview Card */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Preview Tampilan Banner Beranda
              </label>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 text-white shadow-md p-6">
                <div
                  className="absolute inset-0 opacity-[0.10] pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                  }}
                />
                <div className="relative z-10 flex flex-col justify-between gap-4">
                  <div className="max-w-xl">
                    <h1 className="text-xl lg:text-2xl font-bold tracking-tight mb-1.5 leading-tight">
                      {formData['cta_banner_title'] || "Selamat Datang di ASA Link"}
                    </h1>
                    <p className="text-teal-50/90 text-xs lg:text-sm leading-relaxed">
                      {formData['cta_banner_description'] ||
                        "Solusi digital untuk mendukung manajemen usaha Anda, dirancang untuk menyederhanakan operasional dan meningkatkan kontrol bisnis."}
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 bg-white text-teal-700 px-4 py-2 rounded-lg font-semibold text-xs shadow-md">
                      {formData['cta_button_text'] || "Lihat Panduan"}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {allConfigs
              .filter(c => c.key.startsWith('cta_'))
              .map((config) => (
              <div key={config.key}>
                <div className="flex items-center gap-4 mb-2">
                  {renderConfigInput(config)}
                  <button
                    onClick={() => handleSave(config.key)}
                    disabled={saving}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-500">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-700">
                      {config.key === 'cta_banner_title' && 'Judul Banner Sambutan'}
                      {config.key === 'cta_banner_description' && 'Deskripsi Banner Sambutan'}
                      {config.key === 'cta_button_text' && 'Teks Tombol Panduan'}
                      {config.key === 'cta_button_link' && 'Link / URL Tujuan Tombol Panduan'}
                    </p>
                    <p>{config.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User & Tenant Limits */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <SettingsIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">User & Tenant Limits</h2>
            </div>
            <p className="text-gray-600">Configure limits for users and tenants</p>
          </div>
          
          <div className="p-6 space-y-6">
            {allConfigs
              .filter(c => c.key.includes('max_') || c.key.includes('limit_'))
              .map((config) => (
              <div key={config.key}>
                <div className="flex items-center gap-4 mb-2">
                  {renderConfigInput(config)}
                  <button
                    onClick={() => handleSave(config.key)}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-500">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-700">{config.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p>{config.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Info className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">System Information</h2>
            </div>
            <p className="text-gray-600">Current system status and information</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Application Version</h4>
                <p className="text-gray-900">{formData['app_version'] || '1.0.0'}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Environment</h4>
                <p className="text-gray-900">Production</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Last Updated</h4>
                <p className="text-gray-900">{new Date().toLocaleString()}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Maintenance Mode</h4>
                <p className={`${formData['maintenance_mode'] === 'true' ? 'text-red-600' : 'text-green-600'}`}>
                  {formData['maintenance_mode'] === 'true' ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}